import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { X, Eye, EyeOff } from 'lucide-react';
import { cip } from '../assets/assets';
import { useResetPassword } from '../services/auth/auth';
import { useAlert } from '../context/AlertContext';

const ResetPassword = ({ onClose }) => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { mutateAsync: resetPassword } = useResetPassword();
    const setAlert = useAlert();
    
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.newPassword !== formData.confirmPassword) {
            setAlert({ type: 'error', message: 'Passwords do not match' });
            return;
        }

        const passwordRegex = /^(?=(?:[^A-Z]*[A-Z]){3})(?=(?:[^a-z]*[a-z]){2})(?=(?:\D*\d){2})(?=.*[^a-zA-Z0-9]).{8,}$/;
        if (!passwordRegex.test(formData.newPassword)) {
            setAlert({ type: 'error', message: 'Password must be at least 8 characters long, contain at least 3 uppercase letters, 2 lowercase letters, 2 digits, and 1 special character.' });
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await resetPassword({ token, newPassword: formData.newPassword });
            setAlert({ type: 'success', message: response.message || 'Password reset successful!' });
            navigate('/clients/signin');
        } catch (error) {
            const err = error.response?.data?.error || 'Failed to reset password. The link may be invalid or expired.';
            setAlert({ type: 'error', message: err });
            console.log(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="font-['Poppins'] bg-gray-50 min-h-screen flex md:flex-row flex-col">
            {/* Image section */}
            <div className="w-[88%] mt-[4rem] md:mt-0 mx-auto md:w-1/2 md:max-h-screen">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 md:hidden"
                    aria-label="Close"
                >
                    <Link to={"/clients/signin"}>
                        <X size={24} className="text-gray-800" />
                    </Link>
                </button>

                <img
                    src={cip}
                    alt="Person using keyboard"
                    className="w-full h-64 md:h-full object-cover"
                />
            </div>

            {/* Form section */}
            <div className="md:w-1/2 px-6 py-8 md:px-16 md:py-12 md:flex md:flex-col md:justify-center">
                <h1 className="text-2xl font-bold text-[#174568] mb-6">Set New Password</h1>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="newPassword" className="block text-gray-700 mb-2">New Password</label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                id="newPassword"
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleChange}
                                placeholder="Enter new password"
                                className="w-full p-3 border border-[#C1C7CD] md:border-x-0 md:border-t-0 md:bg-[#F2F4F8] bg-transparent rounded-md md:rounded-none focus:outline-none focus:border-[#174568] md:focus:border-b-[#174568] md:focus:border-x-0 md:focus:border-t-0"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                            >
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label htmlFor="confirmPassword" className="block text-gray-700 mb-2">Confirm Password</label>
                        <div className="relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                placeholder="Confirm new password"
                                className="w-full p-3 border border-[#C1C7CD] md:border-x-0 md:border-t-0 md:bg-[#F2F4F8] bg-transparent rounded-md md:rounded-none focus:outline-none focus:border-[#174568] md:focus:border-b-[#174568] md:focus:border-x-0 md:focus:border-t-0"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                            >
                                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                            It must be a combination of minimum 8 letters, 3 uppercase letters, 2 lowercase letters, 2 numbers and 1 symbol.
                        </p>
                    </div>
                    
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-[#174568] hover:bg-gray-800 text-white font-medium py-3 px-4 mt-6 transition-colors disabled:opacity-70 flex justify-center items-center"
                    >
                        {isSubmitting ? 'Resetting...' : 'Reset Password'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;
